/**
 * S7 — Signed Documents section for the Profile page.
 * Lists the signed-in user's own signed-JHA history (GET /api/my-signed-documents):
 * project name, JHA date, linked plan name (if any), and when they signed.
 */
import { useEffect, useState } from 'react';
import { FileSignature } from 'lucide-react';
import { api } from '../../utils/api';
// Badge reaudit R9: calendar-date formatting (no day-shift on date-only JHA dates).
import { formatCalendarDate } from '../../utils/calendarDate';

interface SignedDocument {
  jhaId: number;
  projectId: number;
  projectName: string;
  date: string;
  signedAt: string;
  planName: string | null;
}

export default function SignedDocumentsSection() {
  const [documents, setDocuments] = useState<SignedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/my-signed-documents')
      .then((res: any) => setDocuments(res.data?.data ?? res.data ?? []))
      .catch(() => setError('Could not load your signed documents.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-white rounded-xl shadow border border-gray-100 p-6 mt-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
          <FileSignature className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Signed Documents</h2>
          <p className="text-sm text-gray-500">JHAs you have signed</p>
        </div>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading…</p>}
      {error && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

      {!loading && !error && documents.length === 0 && (
        <p className="text-sm text-gray-500">No signed documents yet.</p>
      )}

      {!loading && documents.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {documents.map((doc) => (
            <li key={`${doc.jhaId}`} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{doc.projectName}</p>
                <p className="text-xs text-gray-500">
                  JHA date {formatCalendarDate(doc.date)}
                  {doc.planName ? ` · Plan: ${doc.planName}` : ''}
                </p>
              </div>
              <div className="text-xs text-gray-600 sm:text-right">
                Signed {new Date(doc.signedAt).toLocaleDateString()}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
